import { UniqueIdentifier } from "@dnd-kit/core";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import InventoryLevelsIcon from "@redotech/redo-icons/inventory_levels.svg";
import { InventoryLevelsSystemView } from "@redotech/redo-inventory-common/views/default-inventory-levels-views";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { InventoryLevelsViewsContext } from "@redotech/redo-merchant-inventory-app/inventory-views";
import { SortableTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/sortable-tree";
import { TreeItem } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/types";
import { flattenTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/utilities";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useMemo } from "react";
import { useMatch } from "react-router-dom";
import {
  FolderWithView,
  treeToViewsAndFolders,
  viewsAndFoldersToTree,
} from "../orders/view-to-tree-utils";
import { useDefaultViewName } from "./get-default-view-name";

export const InventoryLevelsItem = memo(function InventoryLevelsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const {
    views,
    folders,
    openEditFolderModal,
    openCreateFolderModal,
    updateOrdering,
  } = useRequiredContext(InventoryLevelsViewsContext);
  const urlPrefix = `${routes.inventoryManagementBaseUrl}/products`;

  const viewsWithoutFolders = useMemo(() => {
    return views?.filter((view) => !view.folderId) || [];
  }, [views]);

  const defaultViewName = useDefaultViewName(
    views,
    InventoryLevelsSystemView.ALL,
  );

  const foldersWithViews = useMemo<FolderWithView<"inventory-levels">[]>(() => {
    return (
      folders?.map((folder) => {
        return {
          folder,
          views: views?.filter((view) => view.folderId === folder._id) || [],
        };
      }) || []
    );
  }, [folders, views]);

  const sortableItems = useMemo(() => {
    return viewsAndFoldersToTree({
      viewsWithoutFolders,
      foldersWithViews,
      urlPrefix,
      viewCounts: {},
      folderCounts: {},
      hideZeroNotifications: false,
    });
  }, [viewsWithoutFolders, foldersWithViews, urlPrefix]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    openEditFolderModal(id.toString());
  });

  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const flattenedItems = flattenTree(items);
    const result = treeToViewsAndFolders<"inventory-levels">(
      flattenedItems,
      views || [],
      folders || [],
    );
    updateOrdering(result.views, result.folders);
  });

  const anyChildSelected = useMatch(`${urlPrefix}/*`);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${urlPrefix}/${encodeURIComponent(defaultViewName)}`,
      }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={InventoryLevelsIcon}
      friendlyName="Inventory levels"
      id="inventory-levels"
      UnfilledIcon={InventoryLevelsIcon}
    >
      <SortableTree
        items={sortableItems}
        onRequestEdit={handleRequestEdit}
        setItems={handleUpdateOrdering}
      />
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: `${routes.inventoryManagementBaseUrl}/adjustment-report`,
        }}
        friendlyName="Adjustment reports"
        id="inventory-adjustment-reports"
      />
      <RedoSecondaryNavItem
        action={{
          type: "button",
          onClick: () => {
            openCreateFolderModal();
          },
        }}
        friendlyName="+ Add new folder"
        id="inventory-levels-create-folder"
      />
    </RedoSecondaryNavItem>
  );
});
